'use memo';

import { AgxFileUpload } from '@urbanx/agx-ui-components';
import {
  AgxButton,
  AgxModal,
  AgxRadioGroup,
  AgxSpacer,
  AgxTypography,
} from '@urbanx/agx.ui-components2';
import { useMarkedAsExchange } from './useMarkedAsExchange';
import { DocumentTypes } from '@urbanx/agx.campaigns.client';
import { useSelectedCampaign } from 'hooks';
import { deleteFile, getFileLink } from 'services';

interface Props {
  controller: ReturnType<typeof useMarkedAsExchange>;
}

const MarkedAsExchange = ({ controller }: Props) => {
  const {
    getAuthToken,
    campaignId,
    exchangeLetterFiles,
    isConfirmDisabled,
    isOpen,
    isAgentExchange,
    isSolicitorExchange,
    selectedOption,
    setIsDialogOpen,
    setSelectedOption,
    onUploadFile,
    closeModal,
    onConfirm,
    onExchangeLetterChanged,
    onSignedContractChanged,
    signedContractFiles,
  } = controller;

  const { campaign: selectedCampaign } = useSelectedCampaign();
  const uploadCampaignId = campaignId || selectedCampaign?.campaignId || '';

  return (
    <AgxModal
      type={'single'}
      actionButtons={[
        <AgxButton
          key={'btnCancel'}
          id={'btnCancel'}
          text={'Cancel'}
          variant={'hollow'}
          onClick={closeModal}
          stretch
        />,
        <AgxButton
          key={'btnConfirm'}
          id={'btnConfirm'}
          text={'Confirm'}
          variant={'dark'}
          disabled={isConfirmDisabled}
          onClick={() => onConfirm(uploadCampaignId)}
          stretch
        />,
      ]}
      secondaryTitle={
        <AgxSpacer direction="vertical" gap={'spacing-lg'}>
          <AgxTypography
            id="agx-modal-title"
            size="medium"
            type="body_ui_strong"
            dataTestId={`agx-modal-markasexchanged`}
            color="var(--text-primary)"
          >
            {'Mark as Exchanged'}
          </AgxTypography>
          <AgxTypography
            id="agx-modal-title"
            size="small"
            type="body_ui"
            dataTestId={`agx-modal-markasexchanged`}
            color="var(--text-secondary)"
          >
            {isSolicitorExchange
              ? 'Please upload the following files to process'
              : 'Please upload the following file to process'}
          </AgxTypography>
        </AgxSpacer>
      }
      variant={'withDivider'}
      actionButtonContainerStyle={'withoutDivider'}
      setIsDialogOpen={setIsDialogOpen}
      isOpen={isOpen}
      content={
        <AgxSpacer
          direction="vertical"
          padding={{ top: 'md', bottom: 'xl', left: 'xl', right: 'xl' }}
          gap={'md'}
        >
          {isSolicitorExchange && (
            <AgxSpacer direction="vertical" gap={'spacing-lg'}>
              <AgxFileUpload
                id="fileUpload"
                title="Upload the Exchange Letter"
                documentType={DocumentTypes.ExchangeLetter}
                buttonText={'Choose file'}
                fileData={exchangeLetterFiles}
                onValueChanged={onExchangeLetterChanged}
                getAuthToken={getAuthToken}
                campaignId={uploadCampaignId}
                uploadFile={onUploadFile}
                deleteFile={deleteFile}
                getFileLink={getFileLink}
                extensions={['pdf']}
                multiple={false}
                autoUpload={false}
                required
              />
            </AgxSpacer>
          )}
          {isAgentExchange && (
            <AgxSpacer direction="vertical" gap={'spacing-lg'}>
              <AgxFileUpload
                id="signedContractFileUpload"
                title="Signed Contract"
                documentType={DocumentTypes.SignedContract}
                buttonText={'Choose file'}
                fileData={signedContractFiles}
                onValueChanged={onSignedContractChanged}
                getAuthToken={getAuthToken}
                campaignId={uploadCampaignId}
                uploadFile={onUploadFile}
                deleteFile={deleteFile}
                getFileLink={getFileLink}
                extensions={['pdf']}
                multiple={false}
                autoUpload={false}
                required
              />
            </AgxSpacer>
          )}
          {isSolicitorExchange && (
            <AgxRadioGroup
              id={'signedContractYesNo'}
              layout={'vertical'}
              options={[
                {
                  label: 'Yes',
                  value: 'Yes',
                },
                {
                  label: 'No',
                  value: 'No',
                },
              ]}
              label="Have you got a Signed Contract *"
              onChange={(option) => setSelectedOption(option)}
              subContent={
                selectedOption === 'Yes' ? (
                  <AgxSpacer
                    fill
                    direction="vertical"
                    gap={'spacing-xl'}
                    padding={{
                      top: 'spacing-2xl',
                      bottom: 'spacing-2xl',
                      left: 'spacing-lg',
                      right: 'spacing-lg',
                    }}
                  >
                    <AgxSpacer direction="vertical" gap={'spacing-lg'}>
                      <AgxFileUpload
                        id="signedContractFileUpload"
                        title="Signed Contract"
                        documentType={DocumentTypes.SignedContract}
                        buttonText={'Choose file'}
                        fileData={signedContractFiles}
                        onValueChanged={onSignedContractChanged}
                        getAuthToken={getAuthToken}
                        campaignId={uploadCampaignId}
                        uploadFile={onUploadFile}
                        deleteFile={deleteFile}
                        getFileLink={getFileLink}
                        extensions={['pdf']}
                        multiple={false}
                        autoUpload={false}
                        required
                      />
                    </AgxSpacer>
                  </AgxSpacer>
                ) : undefined
              }
              value={selectedOption}
            ></AgxRadioGroup>
          )}
        </AgxSpacer>
      }
    ></AgxModal>
  );
};

export default MarkedAsExchange;
